import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import getLoyaltyPrizeDetail from '../../../api/graphql/queries/getLoyaltyPrizeDetail';
import LoadIcon from '../../LoadIcon';
import { PRIZE_STATUS, formatConversion, formatStock } from '../../../utils/loyaltyPrizes';

const MONTH_SLOTS = 9;

const withCommas = value => Number(value || 0).toLocaleString('en-US');

const formatWhen = (value, pattern) => {
  if (!value) return '—';
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format(pattern || 'DD MMM YYYY') : '—';
};

// The drawer's chart needs 9 labelled slots, one per calendar month of the window the
// backend computed (windowStart..windowEnd, 9 months inclusive). `detail.monthly` only
// carries months that HAD at least one claim — a month with zero claims is simply absent
// from the array, not present with claimed: 0. Mapping over `monthly` directly would
// therefore compress any gap into adjacent bars and draw a compressed, lying time axis.
// Building the slots from `windowStart` and looking each month up (defaulting missing
// months to 0) is the only correct way to render this.
//
// windowStart is a UTC, start-of-month ISO string produced server-side — moment.utc()
// keeps that month boundary stable regardless of the browser's local timezone.
const buildMonthlySlots = (windowStart, monthly) => {
  const claimedByMonth = {};
  (monthly || []).forEach((entry) => {
    if (entry && entry.month) claimedByMonth[entry.month] = entry.claimed || 0;
  });

  const start = windowStart
    ? moment.utc(windowStart)
    : moment.utc().startOf('month').subtract(MONTH_SLOTS - 1, 'months');

  const slots = [];
  for (let i = 0; i < MONTH_SLOTS; i++) {
    const cursor = moment(start).add(i, 'months');
    const key = cursor.format('YYYY-MM');
    slots.push({
      key,
      label: cursor.format('MMM').toUpperCase(),
      // A bucket the backend returned outside this 9-slot window cannot happen in
      // practice (usedAt is only ever stamped inside the window), so any such bucket
      // is simply never looked up here rather than growing a tenth slot for it.
      claimed: claimedByMonth[key] || 0,
    });
  }
  return slots;
};

// The point of the drawer: one line telling the admin what, if anything, needs doing
// about this prize, using the row's own real numbers. `active`/`empty` need no verdict.
const buildVerdict = (row) => {
  const won = withCommas(row.won);
  const codesLeft = withCommas(row.codesLeft);
  const lastClaimAt = formatWhen(row.lastClaimAt);

  switch (row.status) {
    case 'no-codes':
      // "No codes to give them" is only true when nobody has been served yet. A prize
      // like loyalty-prize-my-primavera-card-2024 can be won AND claimed with zero ticketCode
      // docs — those fans are served via AccessTicket, a path this verdict doesn't know
      // about, and uploading a code batch would fill a pool that path never reads. Once
      // claims exist, stay silent rather than give advice that doesn't fit the prize.
      // Such a prize now reports as 'on-demand' instead, once it has at least one
      // ticketCode doc — this stays as the guard for the window before that.
      if (row.claimed === 0) {
        return { tone: 'bad', text: `${won} fans won this and there are no codes to give them. Upload a batch.` };
      }
      return null;
    case 'on-demand':
      return {
        tone: 'quiet',
        text: `${withCommas(row.claimed)} cards issued. AccessTicket mints one per fan on claim, so there is no pool to top up.`,
      };
    case 'sold-out':
      return {
        tone: 'bad',
        text: `Out of stock since ${lastClaimAt}. Fans claiming now get NO_CODES_AVAILABLE, and those misses are not recorded.`,
      };
    case 'low-stock':
      return { tone: 'warn', text: `${codesLeft} codes left.` };
    case 'unclaimed':
      return { tone: 'warn', text: `${won} wins, no claims. Codes are available, so nothing is stopping them.` };
    case 'stalled':
      return { tone: 'quiet', text: `No claims since ${lastClaimAt}. ${codesLeft} codes still unused.` };
    default:
      return null;
  }
};

const LoyaltyPrizeDrawer = ({
  row, onClose, onAddCodes, onSeeWinners,
}) => {
  const [detail, setDetail] = useState(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(true);

  useEffect(() => {
    let mounted = true;
    setIsLoadingDetail(true);
    setDetail(null);
    (async () => {
      const data = await getLoyaltyPrizeDetail(row.slugName);
      if (mounted) {
        setDetail(data);
        setIsLoadingDetail(false);
      }
    })();
    return () => { mounted = false; };
  }, [row.slugName]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const stopPropagation = event => event.stopPropagation();

  const statusMeta = PRIZE_STATUS[row.status] || PRIZE_STATUS.empty;
  const verdict = buildVerdict(row);

  const hasDetail = !!(detail && detail.isAuth);
  const monthlySlots = hasDetail ? buildMonthlySlots(detail.windowStart, detail.monthly) : [];
  const peak = Math.max(1, ...monthlySlots.map(slot => slot.claimed));
  const latestClaims = hasDetail ? (detail.latestClaims || []) : [];

  // Three distinct, non-collapsible states for the fetched half of the drawer (the
  // header/verdict/figures above come from `row` and always render regardless):
  let detailBody;
  if (isLoadingDetail) {
    // 1. LOADING — settled state (success/failure) is unknown yet; show the spinner
    // and render neither the chart nor the failure message.
    detailBody = (
      <div className="pd-sec" style={{ textAlign: 'center' }}>
        <LoadIcon />
      </div>
    );
  } else if (!hasDetail) {
    // 2. SETTLED, FAILED — the fetch errored or came back unauthorised. Say so
    // explicitly instead of silently drawing an empty chart / empty claims list, which
    // would read as "this prize has no claims" right under figures that say otherwise.
    // Mirrors LoyaltyUserModal's "Unable to load ..." pattern.
    detailBody = (
      <div className="pd-sec">
        <div className="pd-empty">Unable to load this prize&apos;s claim detail.</div>
      </div>
    );
  } else {
    // 3. SETTLED, LOADED — real detail data; original behaviour.
    detailBody = (
      <>
        {/* Gate on the chart's own data, not `row.claimed`: `row.claimed` counts
            `loyalty.claimedAt` while `monthlySlots` buckets `ticketCode.usedAt` — two
            different collections that genuinely diverge (measured: 1,689 vs 1,704),
            since setLoyaltyCard stamps claimedAt without touching ticketCode. Gating on
            row.claimed could render nine empty bars under a populated-looking heading. */}
        {monthlySlots.some(slot => slot.claimed > 0) ? (
          <div className="pd-sec">
            <h4>
              Claims per month
              <em>last 9 months</em>
            </h4>
            <div className="pd-months">
              {monthlySlots.map(slot => (
                <div key={slot.key}>
                  <b style={{ height: `${(slot.claimed / peak) * 100}%` }} />
                  <span className="lbl">{slot.label}</span>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <div className="pd-sec">
          <h4>
            Latest claims
            <em>read-only</em>
          </h4>
          {latestClaims.length === 0 ? (
            <div className="pd-empty">Nothing to show yet.</div>
          ) : (
            <table className="pd-claims">
              <tbody>
                {/* Multi-use coupon pools serve one shared code for every claim (21 of
                    56 real pools; gb.csv alone is 49 rows of one code), so `claim.code`
                    alone is not unique across rows here — pair it with `usedAt`. */}
                {latestClaims.map(claim => (
                  <tr key={`${claim.code}-${claim.usedAt}`}>
                    <td className="c">{claim.code}</td>
                    <td>
                      {claim.link ? (
                        <a className="link" href={claim.link} target="_blank" rel="noopener noreferrer">Open ticket ↗</a>
                      ) : '—'}
                    </td>
                    <td className="t">{formatWhen(claim.usedAt, 'DD MMM YYYY · HH:mm')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </>
    );
  }

  return (
    <div className="loyalty">
      <div className="pd-scrim" onClick={onClose}>
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
        <aside
          className="pd-panel"
          role="dialog"
          aria-modal="true"
          aria-label={row.postName || row.slugName}
          onClick={stopPropagation}
        >
          <div className="pd-top">
            <button type="button" className="x" onClick={onClose} aria-label="Close">✕</button>
            <h2>{row.postName || row.slugName}</h2>
            <div className="sub">
              <span>{row.slugName}</span>
              <span className={`chip chip--${statusMeta.tone}`}>{statusMeta.label}</span>
            </div>
            <div className="pd-actions" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {/* Deliberately absent for on-demand prizes. A CSV uploaded into the
                  card's scope would be handed to a fan by loyaltyClaimPrize instead of
                  a real AccessTicket card, and setLoyaltyCard would then see the row as
                  already issued and never mint one. */}
              {!row.onDemand && (
                <button type="button" className="btn p" onClick={() => onAddCodes(row)}>＋ Add codes</button>
              )}
              {/* Codes stay fan-anonymous (see the "Latest claims" table below, and
                  buildLatestClaimsPipeline, which deliberately never projects userId) —
                  this is the drawer's route to the fans themselves, via the leaderboard's
                  existing slug/type filter (see LoyaltyContainer.jsx's handleSelectPrizeWinners). */}
              <button
                type="button"
                className="btn"
                disabled={row.won === 0}
                onClick={() => onSeeWinners(row)}
              >
                {row.won === 0
                  ? 'No fans have won this yet'
                  : `See the ${withCommas(row.won)} fans who won this →`}
              </button>
            </div>
          </div>

          {verdict ? (
            <div className={`pd-verdict pd-verdict--${verdict.tone}`}>{verdict.text}</div>
          ) : null}

          <div className="pd-sec">
            <h4>
              Figures
              <em>{formatStock(row)}</em>
            </h4>
            <div className="pd-figs">
              <div>
                <span className="label">Won</span>
                <span className="v">{withCommas(row.won)}</span>
              </div>
              <div>
                <span className="label">Claimed</span>
                <span className="v">{withCommas(row.claimed)}</span>
              </div>
              <div>
                <span className="label">Conversion</span>
                <span className="v">{formatConversion(row)}</span>
              </div>
              <div>
                <span className="label">Codes uploaded</span>
                <span className="v">{row.codesTotal ? withCommas(row.codesTotal) : '—'}</span>
              </div>
              <div>
                <span className="label">Used</span>
                <span className="v">{row.codesTotal ? withCommas(row.codesUsed) : '—'}</span>
              </div>
              <div>
                <span className="label">Left</span>
                <span className="v">{row.codesTotal ? withCommas(row.codesLeft) : '—'}</span>
              </div>
              <div>
                <span className="label">Imported</span>
                <span className="v">{formatWhen(row.lastUploadedAt)}</span>
              </div>
            </div>
          </div>

          {detailBody}

          <div className="pd-foot">
            Read-only · codes admin-only · not included in CSV export
          </div>
        </aside>
      </div>
    </div>
  );
};

LoyaltyPrizeDrawer.propTypes = {
  row: PropTypes.shape({
    slugName: PropTypes.string.isRequired,
    postName: PropTypes.string,
    status: PropTypes.string,
    onDemand: PropTypes.bool,
    won: PropTypes.number,
    claimed: PropTypes.number,
    conversion: PropTypes.number,
    codesTotal: PropTypes.number,
    codesUsed: PropTypes.number,
    codesLeft: PropTypes.number,
    lastClaimAt: PropTypes.string,
    lastUploadedAt: PropTypes.string,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onAddCodes: PropTypes.func.isRequired,
  onSeeWinners: PropTypes.func.isRequired,
};

export default LoyaltyPrizeDrawer;
